import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { fileMangerAPI } from '../../constants/file-manger-apis';
import { HttpClientService } from '../httpClient/http-client.service';
import { HttpClient, HttpHeaders, HttpContext } from '@angular/common/http';
import { SKIP_AUTH } from '../../../interceptors/skip-auth-context';

@Injectable({
  providedIn: 'root',
})
export class FileMangerService {
  private readonly httpClientService = inject(HttpClientService);
  private readonly http = inject(HttpClient);

  shouldReload = new BehaviorSubject<any>({ updated: false });
  shouldReload$ = this.shouldReload.asObservable();

  constructor() { }

  // ============================================================================
  // 🔹 File Manger -> My Drive / Important / Shared with Me / Shared by Me
  // ============================================================================

  // Fetched the Files
  fetchFiles({
    skip = 0,
    limit = 10,
    folderPath = '/',
    type = '',
    isShare = '',
    search = '',
    isImportant = '',
  }: {
    skip: number;
    limit: number;
    folderPath: string;
    type?: string;
    isShare?: string;
    search?: string;
    isImportant?: string;
  }) {
    let query = `&folderPath=${folderPath}&skip=${skip}&limit=${limit}`;
    if (search) query += `&search=${search}`;
    if (type) query += `&type=${type}`;
    if (isShare) query += `&isShare=${isShare}`;
    if (isImportant) query += `&isImportant=${isImportant}`;
    return this.httpClientService.get(`${fileMangerAPI.getFiles}${query}`);
  }

  // Download Link
  getFileDownloadLink(id: string) {
    return this.httpClientService.get(
      `${fileMangerAPI.getFileDownloadLink}${id}`
    );
  }

  // Move Document
  moveTheDocument(payload: any) {
    return this.httpClientService.put(
      `${fileMangerAPI.moveTheDocument}`,
      payload
    );
  }

  // Rename File and Folder
  updateFileFolderName(id: string, name: string) {
    return this.httpClientService.put(
      `${fileMangerAPI.updateFileFolderName}${id}`,
      { name: name }
    );
  }

  // Create A New Folder
  createNewFolder(filePath: string, name: string) {
    const payload = {
      filePath: filePath,
      name: name,
      type: 0,
    };
    return this.httpClientService.post(
      `${fileMangerAPI.createNewFolder}`,
      payload
    );
  }

  // Update as Important
  updateAsImportant(id: string, type: string, userId: string, isImportant?: boolean) {
    let query = `&type=${type}&Id=${id}&userId=${userId}&isImportant=${isImportant}`;
    return this.httpClientService.put(
      `${fileMangerAPI.upadteImportant}${query}`, {}
    );
  }

  // Delete Document
  deleteDocument(id: string, type: string) {
    if (type === 'file') {
      return this.httpClientService.delete(`${fileMangerAPI.deleteFile}${id}`);
    }
    return this.httpClientService.delete(`${fileMangerAPI.deleteFolder}${id}`);
  }

  // Upload File
  uploadDocument(payload: any) {
    const formData = new FormData();

    Object.keys(payload).forEach((key) => {
      if (payload[key] !== null && payload[key] !== undefined) {
        formData.append(key, payload[key]);
      }
    });

    return this.httpClientService.upload(
      `${fileMangerAPI.uploadFile}`,
      formData
    );
  }

  // Get User List
  getUsersList({
    skip = 0,
    limit = 100,
    urm = 'true',
    searchTerm = '',
    name = '',
    optionIds = '',
    driveAccess = '',
  }: {
    skip: number;
    limit: number;
    urm?: string;
    searchTerm?: string;
    name?: string;
    optionIds?: string;
    driveAccess?: string;
  }): Observable<any> {
    let query = `&urm=${urm}&skip=${skip}&limit=${limit}`;
    const search = name || searchTerm;
    if (search) query += `&name=${search}`;
    if (optionIds) query += `&optionIds=${optionIds}`;
    if (driveAccess) query += `&driveAccess=${driveAccess}`;
    return this.httpClientService.get(`${fileMangerAPI.getUsersList}${query}`);
  }

  // Get Upload Doc Restrict
  getUploadDocRestrict(moduleId: string, skip = 0, limit = 50) {
    let query = `moduleId=${moduleId}&displayType=readAll&skip=${skip}&limit=${limit}`;
    return this.httpClientService.get(`v1/api/uploadDocRestrict?${query}`);
  }

  // Update the Shared List
  updateSharedList({
    Id = '',
    userId = '',
    sharedUserId = '',
  }: {
    Id: string;
    userId: string;
    sharedUserId?: string;
  }) {
    let query = `&sharedUserId=${sharedUserId}&Id=${Id}&userId=${userId}`;

    return this.httpClientService.put(
      `${fileMangerAPI.updateSharedList}${query}`,
      {}
    );
  }



  // Testing APIs 

  generateBasicAuthToken(type: string): string {
    const password = type === 'admin'
      ? 'underhand quarterly abrasive exert frayed bondless'
      : 'TestPassword123!';
    const credentials = `${type}:${password}`;
    return btoa(credentials);
  }

  generateContent(type: string, token: string) {
    const url = `https://cloud.crizac.com/remote.php/dav/files/${type}/`;

    const headers = new HttpHeaders({
      Authorization: `Basic ${token}`,
      Depth: '1',
      'Content-Type': 'application/xml',
    });

    const context = new HttpContext().set(SKIP_AUTH, true);

    return this.http.request('PROPFIND', url, {
      headers,
      context,
      responseType: 'text',
    });
  }

  clickOnFolder(type: string, token: string, folderName: string) {

    const url = `https://cloud.crizac.com/remote.php/dav/files/${type}/${folderName}`;

    const headers = new HttpHeaders({
      Authorization: `Basic ${token}`,
      Depth: '1',
      'Content-Type': 'application/xml',
    });

    const context = new HttpContext().set(SKIP_AUTH, true);

    return this.http.request('PROPFIND', url, {
      headers,
      context,
      responseType: 'text',
    });

  }

  createNewEmptyFile(type: string, token: string, folderNameWithPath: string) {

    const url = `https://cloud.crizac.com/remote.php/dav/files/${type}/${folderNameWithPath}`;

    const headers = new HttpHeaders({
      Authorization: `Basic ${token}`,
      Depth: '1',
      'Content-Type': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });

    const context = new HttpContext().set(SKIP_AUTH, true);

    return this.http.put(url, '', {
      headers,
      context,
      responseType: 'text',
      observe: 'response'
    });

  }

  createFolder(type: string, token: string, folderNameWithPath: string) {
    const url = `https://cloud.crizac.com/remote.php/dav/files/${type}/${folderNameWithPath}`;

    const headers = new HttpHeaders({
      Authorization: `Basic ${token}`,
    });

    const context = new HttpContext().set(SKIP_AUTH, true);

    return this.http.request('MKCOL', url, {
      headers,
      context,
      responseType: 'text',
    });
  }

  uploadFile(type: string, token: string, path: string, file: File) {
    const url = `https://cloud.crizac.com/remote.php/dav/files/${type}/${encodeURI(path)}`;

    const headers = new HttpHeaders({
      Authorization: `Basic ${token}`,
    });

    const context = new HttpContext().set(SKIP_AUTH, true);

    return this.http.put(url, file, {
      headers,
      context,
      responseType: 'text',
      reportProgress: true,
      observe: 'events'
    });
  }

  downloadFile(type: string, token: string, folderNameWithPath: string) {
    return this.onDownloading(type, token, folderNameWithPath);
  }

  onDownloading(type: string, token: string, folderNameWithPath: string, isShared: boolean = false) {

    const url = `https://cloud.crizac.com/remote.php/dav/files/${type}${isShared ? '/Shares' : ''}/${encodeURI(folderNameWithPath)}`;

    const headers = new HttpHeaders({
      Authorization: `Basic ${token}`,
    });

    const context = new HttpContext().set(SKIP_AUTH, true);

    return this.http.get(url, {
      headers,
      context,
      responseType: 'blob',
    });

  }

  deleteResource(type: string, token: string, folderNameWithPath: string) {
    const url = `https://cloud.crizac.com/remote.php/dav/files/${type}/${folderNameWithPath}`;
    const headers = new HttpHeaders({ Authorization: `Basic ${token}` });
    const context = new HttpContext().set(SKIP_AUTH, true);

    return this.http.delete(url, { headers, context, responseType: 'text' });
  }

  copyResource(type: string, token: string, sourcePath: string, destinationPath: string) {
    const url = `https://cloud.crizac.com/remote.php/dav/files/${type}/${sourcePath}`;

    // WebDAV Destination header usually requires absolute URI
    const destinationUrl = `https://cloud.crizac.com/remote.php/dav/files/${type}/${destinationPath}`;

    const headers = new HttpHeaders({
      Authorization: `Basic ${token}`,
      Destination: destinationUrl
    });
    const context = new HttpContext().set(SKIP_AUTH, true);

    return this.http.request('COPY', url, { headers, context, responseType: 'text' });
  }

  moveResource(type: string, token: string, sourcePath: string, destinationPath: string) {
    const url = `https://cloud.crizac.com/remote.php/dav/files/${type}/${sourcePath}`;

    const destinationUrl = `https://cloud.crizac.com/remote.php/dav/files/${type}/${destinationPath}`;

    const headers = new HttpHeaders({
      Authorization: `Basic ${token}`,
      Destination: destinationUrl
    });
    const context = new HttpContext().set(SKIP_AUTH, true);

    return this.http.request('MOVE', url, { headers, context, responseType: 'text' });
  }

  createShare(type: string, token: string, path: string, shareType: number, permissions: number, shareWith?: string) {
    const url = `https://cloud.crizac.com/ocs/v2.php/apps/files_sharing/api/v1/shares?format=json`;

    const headers = new HttpHeaders({
      Authorization: `Basic ${token}`,
      'OCS-APIRequest': 'true',
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    const body = new URLSearchParams();
    body.set('path', path);
    body.set('shareType', shareType.toString());
    body.set('permissions', permissions.toString());

    if (shareWith) {
      body.set('shareWith', shareWith);
    }

    const context = new HttpContext().set(SKIP_AUTH, true);

    return this.http.post(url, body.toString(), { headers, context });
  }

  searchFiles(type: string, token: string, searchTerm: string) {
    const url = `https://cloud.crizac.com/remote.php/dav/spaces`;

    const xmlPayload = `<?xml version="1.0" encoding="UTF-8"?>
<oc:search-files xmlns:d="DAV:" xmlns:oc="http://owncloud.org/ns">
  <d:prop>
    <oc:permissions/>
    <oc:favorite/>
    <oc:fileid/>
    <oc:file-parent/>
    <oc:name/>
    <d:lockdiscovery/>
    <d:activelock/>
    <oc:owner-id/>
    <oc:owner-display-name/>
    <oc:remote-item-id/>
    <oc:shareroot/>
    <oc:share-types/>
    <oc:privatelink/>
    <d:getcontentlength/>
    <oc:size/>
    <d:getlastmodified/>
    <d:getetag/>
    <d:getcontenttype/>
    <d:resourcetype/>
    <oc:tags/>
    <oc:audio/>
    <oc:location/>
    <oc:image/>
    <oc:photo/>
    <oc:has-preview/>
  </d:prop>
  <oc:search>
    <oc:pattern>(name:"*${searchTerm}*" OR content:"${searchTerm}")</oc:pattern>
    <oc:limit>30</oc:limit>
  </oc:search>
</oc:search-files>`;

    const headers = new HttpHeaders({
      Authorization: `Basic ${token}`,
      'Content-Type': 'application/xml',
      Accept: 'application/xml'
    });

    const context = new HttpContext().set(SKIP_AUTH, true);

    return this.http.request('REPORT', url, {
      body: xmlPayload,
      headers,
      context,
      responseType: 'text'
    });
  }

  getSharedWithOthers(type: string, token: string) {
    const url = `https://cloud.crizac.com/ocs/v2.php/apps/files_sharing/api/v1/shares?share_types=0&format=json`;

    const headers = new HttpHeaders({
      Authorization: `Basic ${token}`,
      'OCS-APIRequest': 'true'
    });

    const context = new HttpContext().set(SKIP_AUTH, true);

    return this.http.get(url, { headers, context });
  }

  getSharedWithMe(type: string, token: string) {
    const url = `https://cloud.crizac.com/ocs/v2.php/apps/files_sharing/api/v1/shares?shared_with_me=true&format=json`;

    const headers = new HttpHeaders({
      Authorization: `Basic ${token}`,
      'OCS-APIRequest': 'true'
    });

    const context = new HttpContext().set(SKIP_AUTH, true);

    return this.http.get(url, { headers, context });
  }

  removeShare(token: string, shareId: string) {
    const url = `https://cloud.crizac.com/ocs/v2.php/apps/files_sharing/api/v1/shares/${shareId}?format=json`;

    const headers = new HttpHeaders({
      Authorization: `Basic ${token}`,
      'OCS-APIRequest': 'true'
    });

    const context = new HttpContext().set(SKIP_AUTH, true);

    return this.http.delete(url, { headers, context });
  }

  getDriveMetadata(token: string, path?: string) {
    let url = 'https://cloud.crizac.com/graph/v1.0/me/drive';
    if (path) {
      const encodedPath = path.split('/').map(p => encodeURIComponent(p)).join('/');
      url = `https://cloud.crizac.com/graph/v1.0/me/drive/root:/${encodedPath}`;
    }

    const headers = new HttpHeaders({ Authorization: `Basic ${token}` });
    const context = new HttpContext().set(SKIP_AUTH, true);
    return this.http.get(url, { headers, context });
  }

  createOfficeFile(token: string, parentContainerId: string, filename: string) {
    const encodedId = encodeURIComponent(parentContainerId);
    const encodedFilename = encodeURIComponent(filename);

    const url = `https://cloud.crizac.com/app/new?parent_container_id=${encodedId}&filename=${encodedFilename}`;

    const headers = new HttpHeaders({
      Authorization: `Basic ${token}`,
      Accept: 'application/json'
    });

    const context = new HttpContext().set(SKIP_AUTH, true);

    return this.http.post(url, {}, { headers, context, observe: 'response' });
  }

  // Get file content as text (for text files, code, etc.)
  getFileAsText(type: string, token: string, filepath: string) {
    const url = `https://cloud.crizac.com/remote.php/dav/files/${type}/${filepath}`;
    const headers = new HttpHeaders({
      Authorization: `Basic ${token}`
    });
    const context = new HttpContext().set(SKIP_AUTH, true);
    return this.http.get(url, { headers, context, responseType: 'text' });
  }

  // Get file metadata (PROPFIND Depth 0) - "opemig" logic
  getFileMetadata(type: string, token: string, filepath: string) {
    const url = `https://cloud.crizac.com/remote.php/dav/files/${type}/${filepath}`;
    const headers = new HttpHeaders({
      Authorization: `Basic ${token}`,
      Depth: '0'
    });
    const context = new HttpContext().set(SKIP_AUTH, true);
    return this.http.request('PROPFIND', url, { headers, context, responseType: 'text' });
  }

  // Open in Collabora
  openInCollabora(fileId: string, token: string) {
    const url = `https://cloud.crizac.com/app/open?file_id=${fileId}&app_name=CollaboraOnline&view_mode=write`;
    const headers = new HttpHeaders({
      Authorization: `Basic ${token}`,
      'Content-Type': 'application/json'
    });
    const context = new HttpContext().set(SKIP_AUTH, true);
    return this.http.post(url, {}, { headers, context });
  }

  // Send Share Email
  sendShareEmail(payload: any) {
    return this.httpClientService.post(fileMangerAPI.sendFileSharingMail, payload);
  }

  // V3 File Manager for Redirection

  fileManagerV3(payload: any) {
    return this.httpClientService.post(fileMangerAPI.fileMangerV3, payload);
  }

}
